import { RentPaidFields } from '@features/contracts/components/RentPaidFields'
import { useRentDelete } from '@services/contracts/mutations/useRentDelete'
import { useRentUpdate } from '@services/contracts/mutations/useRentUpdate'
import { Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RentModel } from 'types/api'

import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'

export function EditRentFormModal(props: {
  rentId: string
  initialValues?: RentModel
  onSuccess?: () => void
}) {
  const { rentId, initialValues, onSuccess } = props
  const updateRent = useRentUpdate()
  const deleteRent = useRentDelete()
  const { confirm, dialog } = useConfirm()
  const [open, setOpen] = useState(false)
  const { t } = useTranslation('contract')

  const [paidDate, setPaidDate] = useState<string | undefined>(
    initialValues?.paid_date ?? undefined
  )
  const [paid, setPaid] = useState<boolean>(!!initialValues?.paid)
  const [description, setDescription] = useState<string>(
    initialValues?.description ?? ''
  )

  const reset = () => {
    setPaidDate(initialValues?.paid_date ?? undefined)
    setPaid(!!initialValues?.paid)
    setDescription(initialValues?.description ?? '')
  }

  const handleSubmit = () => {
    updateRent.mutate(
      {
        // Send only the edited fields — spreading the whole rent echoed
        // empty values (e.g. a missing specific_symbol) back to the API,
        // which rejected them as blank.
        id: rentId,
        body: {
          paid,
          paid_date: paidDate ?? null,
          description
        } as Partial<RentModel>
      },
      {
        onSuccess: () => {
          setOpen(false)
          onSuccess?.()
        }
      }
    )
  }

  const handleDelete = async () => {
    if (
      !(await confirm({
        title: t('edit_modal.delete_title'),
        description: t('edit_modal.delete_description'),
        confirmText: t('edit_modal.delete'),
        cancelText: t('edit_modal.cancel'),
        destructive: true
      }))
    )
      return
    deleteRent.mutate(rentId, {
      onSuccess: () => {
        setOpen(false)
        onSuccess?.()
      }
    })
  }

  const busy = updateRent.isPending || deleteRent.isPending

  return (
    <>
      {dialog}
      <Button variant="ghostDark" onClick={() => setOpen(true)}>
        <Pencil className="h-4 w-4" />
        {t('edit_modal.edit')}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (next) reset()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Pencil className="h-5 w-5" />
              </span>
              <DialogTitle>{t('edit_modal.edit_rent')}</DialogTitle>
            </div>
          </DialogHeader>

          <RentPaidFields
            paid={paid}
            onPaidChange={setPaid}
            paidDate={paidDate}
            onPaidDateChange={setPaidDate}
            description={description}
            onDescriptionChange={setDescription}
          />

          <DialogFooter className="sm:justify-between">
            <Button
              variant="ghostDark"
              className="text-destructive"
              onClick={() => void handleDelete()}
              disabled={busy}
            >
              <Trash2 className="h-4 w-4" />
              {t('edit_modal.delete')}
            </Button>
            <div className="flex gap-2">
              <Button variant="ghostDark" onClick={() => setOpen(false)}>
                {t('edit_modal.cancel')}
              </Button>
              <Button onClick={handleSubmit} disabled={busy}>
                {t('edit_modal.edit')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
